import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import "./welcome.css";
import { loginUser } from "../loginSlice";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { sendReport } from "../../functions/sendReport";
import bgImg from "./img/BG.png";
import bgImgMobile from "./img/BG-mob.png";
import topBannerImg from "./img/TOP_BANNER.png";
import top2Img from "./img/top2.png";
import top3Img from "./img/top3.png";
import top4Img from "./img/top4.png";
import topMobImg from "./img/top-mob.png";
import formRibbonImg from "./img/ds.png";
import cardRibbonImg from "./img/ms.png";
import awesomeGif from "./img/Awesome.gif";

// Same breakpoint as old/welcome.php's .bg rule (BG.png vs BG-mob.png).
const MOBILE_MQ = "(max-width: 768px)";
const CHILD_NAME_MAX = 30;
const MESSAGE_MAX = 200;
const MIN_TEXT = 3;
const SUCCESS_POPUP_MS = 3000;
// Posting a wish isn't timed, so every save/report carries a fixed time.
const WISH_TIME = "00:00";

function useMediaQuery(query) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia(query);
    const sync = () => setMatches(mq.matches);
    sync();
    if (mq.addEventListener) mq.addEventListener("change", sync);
    else mq.addListener(sync);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener("change", sync);
      else mq.removeListener(sync);
    };
  }, [query]);

  return matches;
}

/** Same right-click/devtools-shortcut block as the other pages. */
function useDisableInspect() {
  useEffect(() => {
    const blockContextMenu = (event) => event.preventDefault();
    const blockShortcutKeys = (e) => {
      if (e.keyCode === 123) return false; // F12
      if (e.ctrlKey && e.shiftKey && e.keyCode === 73) return false; // Ctrl+Shift+I
      if (e.ctrlKey && e.shiftKey && e.keyCode === 74) return false; // Ctrl+Shift+J
      if (e.ctrlKey && e.keyCode === 85) return false; // Ctrl+U
      return true;
    };
    document.addEventListener("contextmenu", blockContextMenu);
    document.addEventListener("keydown", blockShortcutKeys);
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu);
      document.removeEventListener("keydown", blockShortcutKeys);
    };
  }, []);
}

/**
 * old/welcome.php's "Employee Name" value: guests show the email prefix (or
 * employee id when they have neither email nor name); everyone else shows
 * their full name.
 */
function getDisplayName(user) {
  if (!user) return "";
  if (user.role === "GUEST_USER") {
    const email = String(user.email || "").trim();
    if (email.includes("@")) return email.split("@")[0];
    return String(user.name || user.employeeId || "").trim();
  }
  return String(user.name || "").trim();
}

/** PHP ucwords(): capitalise the first letter of each word. */
function ucwords(value) {
  return String(value ?? "").replace(/(^|\s)(\S)/g, (_, sp, ch) => sp + ch.toUpperCase());
}

/** old/welcome.php's pagination: first 10 pages, "...", then the last 4. */
function getPageItems(pageCount) {
  const items = [];
  for (let i = 1; i <= pageCount; i++) {
    if (i <= 10 || i > pageCount - 4) items.push(i - 1);
    else if (i === 11) items.push("ellipsis");
  }
  return items;
}

const EMPTY_FORM = { cname: "", cage: "", message: "" };

function Welcome() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, status } = useSelector((state) => state.auth);
  const adminToken = useSelector(selectAdminToken);
  const isMobile = useMediaQuery(MOBILE_MQ);

  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [showAwesome, setShowAwesome] = useState(false);
  const [wishes, setWishes] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const [wishesLoaded, setWishesLoaded] = useState(false);
  const submitLockRef = useRef(false);

  const page = Math.max(0, parseInt(searchParams.get("q") || "0", 10) || 0);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
  const isDemo = user?.role === "demobypass" || user?.sessionId === "demobypass";

  useDisableInspect();

  // A hard refresh on /welcome loses redux auth — rehydrate from the session.
  useEffect(() => {
    if (!user && status !== "loading") dispatch(loginUser());
  }, [user, status, dispatch]);

  // old/welcome.php bounced back to index.php without a logged-in user.
  useEffect(() => {
    if (status === "failed") navigate("/login", { replace: true });
  }, [status, navigate]);

  // old/welcome.php's back button returned to the intro page.
  useEffect(() => {
    dispatch(setBackButtonUrl("/login?save=true"));
  }, [dispatch]);

  useEffect(() => {
    const body = document.body;
    const prev = {
      backgroundImage: body.style.backgroundImage,
      backgroundSize: body.style.backgroundSize,
      backgroundRepeat: body.style.backgroundRepeat,
      backgroundPosition: body.style.backgroundPosition,
      backgroundAttachment: body.style.backgroundAttachment,
      backgroundColor: body.style.backgroundColor,
    };
    body.classList.remove("common-bg");
    body.style.backgroundImage = `url("${isMobile ? bgImgMobile : bgImg}")`;
    body.style.backgroundSize = "100% 100%";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundPosition = "center center";
    body.style.backgroundAttachment = "fixed";
    body.style.backgroundColor = "";
    return () => {
      Object.assign(body.style, prev);
    };
  }, [isMobile]);

  const loadWishes = useCallback(async () => {
    if (!backendBase || !adminToken) return;
    try {
      const res = await fetch(`${backendBase}/getWishes`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({ page }),
      });
      const data = await res.json().catch(() => ({}));
      if (data.success) {
        setWishes(Array.isArray(data.wishes) ? data.wishes : []);
        setPageCount(Number(data.pageCount) || 0);
      } else {
        setWishes([]);
        setPageCount(0);
      }
    } catch (err) {
      console.error("getWishes:", err);
      setWishes([]);
      setPageCount(0);
    } finally {
      setWishesLoaded(true);
    }
  }, [backendBase, adminToken, page]);

  useEffect(() => {
    loadWishes();
  }, [loadWishes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    let next = value;
    if (name === "cname" || name === "message") next = next.replace(/^\s+/, "");
    if (name === "message") next = next.slice(0, MESSAGE_MAX);
    if (name === "cname") next = next.slice(0, CHILD_NAME_MAX);
    setForm((f) => ({ ...f, [name]: next }));
  };

  const goToPage = (p) => {
    if (p < 0 || (pageCount > 0 && p > pageCount - 1) || p === page) return;
    setSearchParams(p === 0 ? {} : { q: String(p) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitLockRef.current || !user) return;

    const cname = form.cname.trim();
    const message = form.message.trim();
    const cage = parseInt(form.cage, 10);
    if (cname.length < MIN_TEXT) {
      Swal.fire("Child's name must be at least 3 characters", "", "warning");
      return;
    }
    if (!Number.isFinite(cage) || cage < 1 || cage > 18) {
      Swal.fire("Child's age must be between 1 and 18", "", "warning");
      return;
    }
    if (message.length < MIN_TEXT) {
      Swal.fire("Your child's wish must be at least 3 characters", "", "warning");
      return;
    }

    // old/savedata.php answered demo sessions with "1010" → upsell to a plan.
    if (isDemo) {
      await Swal.fire("Subscribe to any PLAN to play with your peers.", "", "success");
      window.location.href = `${process.env.REACT_APP_BASE_URL || ""}/plans`;
      return;
    }

    submitLockRef.current = true;
    setSubmitting(true);
    const name = getDisplayName(user);
    const time = WISH_TIME;

    try {
      const res = await fetch(`${backendBase}/saveWish`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${adminToken}` },
        body: JSON.stringify({
          userId: user.userId,
          email: user.email,
          empId: user.employeeId || user.emp_id || "",
          role: user.role,
          name,
          cname,
          cage,
          message,
          time,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!data.success) {
        throw new Error(data.message || "Something went wrong");
      }

      // old/savedata.php added a fresh report row for every wish.
      sendReport({
        sessionId: user.sessionId,
        organizationId: user.organizationId,
        role: user.role,
        token: user.token || "",
        points: 0,
        time,
        round: message,
        gameId: user.gameId,
        name,
        userId: user.userId,
      }).catch((err) => console.error("sendReport:", err));

      setShowAwesome(true);
      window.setTimeout(() => {
        setShowAwesome(false);
        setForm(EMPTY_FORM);
        submitLockRef.current = false;
        setSubmitting(false);
        loadWishes();
      }, SUCCESS_POPUP_MS);
    } catch (err) {
      console.error("saveWish:", err);
      submitLockRef.current = false;
      setSubmitting(false);
      Swal.fire(err.message || "Something went wrong", "", "error");
    }
  };

  if (!user) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader"></div>
      </div>
    );
  }

  return (
    <div className="welcome-page">
      <div className="welcome-hero welcome-desk">
        <img src={topBannerImg} className="welcome-banner" alt="My Company Plays Santa" />
        <img src={top2Img} className="welcome-top" alt="" />
        <img src={top3Img} className="welcome-top welcome-top--spaced" alt="" />
        <img src={top4Img} className="welcome-top welcome-top--spaced" alt="" />
      </div>
      <div className="welcome-hero welcome-mob">
        <img src={topMobImg} className="welcome-top welcome-top--spaced" alt="My Company Plays Santa" />
      </div>

      <div className="wish-form-card">
        <img src={formRibbonImg} className="wish-form-ribbon" alt="" />
        <form className="wish-form" onSubmit={handleSubmit} autoComplete="off">
          <div className="wish-form-row">
            <label className="wish-label" htmlFor="name">Employee Name:</label>
            <input
              id="name"
              name="name"
              type="text"
              className="wish-input"
              value={getDisplayName(user)}
              readOnly
            />
          </div>
          <div className="wish-form-row">
            <label className="wish-label" htmlFor="cname">Child's Name:</label>
            <input
              id="cname"
              name="cname"
              type="text"
              className="wish-input"
              maxLength={CHILD_NAME_MAX}
              minLength={MIN_TEXT}
              title="Must be at least 3 characters and not start with a space"
              required
              value={form.cname}
              onChange={handleChange}
            />
          </div>
          <div className="wish-form-row">
            <label className="wish-label" htmlFor="cage">Child's Age:</label>
            <input
              id="cage"
              name="cage"
              type="number"
              className="wish-input no-spin"
              min={1}
              max={18}
              required
              value={form.cage}
              onChange={handleChange}
            />
          </div>
          <div className="wish-form-row">
            <label className="wish-label" htmlFor="message">Your Child's Wish:</label>
            <div className="wish-field">
              <textarea
                id="message"
                name="message"
                rows={3}
                className="wish-input"
                maxLength={MESSAGE_MAX}
                minLength={MIN_TEXT}
                title="Must be at least 3 characters and not start with a space"
                required
                value={form.message}
                onChange={handleChange}
              />
              <span className="wish-limit">
                Maximum {form.message.length} / {MESSAGE_MAX} characters allowed
              </span>
            </div>
          </div>

          <div className="wish-submit-wrap">
            {!submitting && (
              <button type="submit" className="wish-submit">
                Submit
              </button>
            )}
          </div>
        </form>
      </div>

      <div className="wish-wall">
        {wishesLoaded && wishes.length === 0 ? (
          <div className="wish-empty">No Post. Be the first one.</div>
        ) : (
          <div className="wish-grid">
            {wishes.map((w) => (
              <div className="wish-card" key={w.id}>
                <img src={cardRibbonImg} className="wish-card-ribbon" alt="" />
                <div className="wish-card-line wish-card-line--first">
                  <b>Employee name:-</b> <span>{ucwords(w.name)}</span>
                </div>
                <div className="wish-card-line">
                  Child name:- <span>{ucwords(w.childName)}</span>
                </div>
                <div className="wish-card-line">
                  Child age:- <span>{w.childAge}</span>
                </div>
                <div className="wish-card-line wish-card-message">
                  Child Wish:- <span>{ucwords(w.childMessage)}</span>
                </div>
              </div>
            ))}
          </div>
        )}

        {pageCount > 0 && (
          <ul className="wish-pagination">
            <li className={page === 0 ? "disabled" : ""}>
              <button type="button" onClick={() => goToPage(page - 1)} disabled={page === 0}>
                Previous
              </button>
            </li>
            {getPageItems(pageCount).map((item) =>
              item === "ellipsis" ? (
                <li className="disabled" key="ellipsis">
                  <span>...</span>
                </li>
              ) : (
                <li className={item === page ? "active" : ""} key={item}>
                  <button type="button" onClick={() => goToPage(item)}>
                    {item + 1}
                  </button>
                </li>
              )
            )}
            <li className={page >= pageCount - 1 ? "disabled" : ""}>
              <button
                type="button"
                onClick={() => goToPage(page + 1)}
                disabled={page >= pageCount - 1}
              >
                Next
              </button>
            </li>
          </ul>
        )}
      </div>

      {showAwesome && (
        <div className="awesome-backdrop">
          <img src={awesomeGif} className="awesome-gif" alt="Awesome!" />
        </div>
      )}
    </div>
  );
}

export default Welcome;
